/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
/* jslint esnext: true */
import IntlMessageFormat from 'intl-messageformat';
import diff from './diff';
// -----------------------------------------------------------------------------
var FIELDS = [
    "second" /* second */,
    "second-short" /* secondShort */,
    "minute" /* minute */,
    "minute-short" /* minuteShort */,
    "hour" /* hour */,
    "hour-short" /* hourShort */,
    "day" /* day */,
    "day-short" /* dayShort */,
    "month" /* month */,
    "month-short" /* monthShort */,
    "year" /* year */,
    "year-short" /* yearShort */
];
function isValidUnits(units) {
    if (!units || ~FIELDS.indexOf(units)) {
        return true;
    }
    if (typeof units === 'string') {
        var suggestion = /s$/.test(units) && units.substr(0, units.length - 1);
        if (suggestion && ~FIELDS.indexOf(suggestion)) {
            throw new Error("\"" + units + "\" is not a valid IntlRelativeFormat 'units' value, did you mean: " + suggestion);
        }
    }
    throw new Error("\"" + units + "\" is not a valid IntlRelativeFormat 'units' value, it must be one of: " + FIELDS.join('", "'));
}
function resolveLocale(locales) {
    if (locales === void 0) { locales = []; }
    if (typeof locales === 'string') {
        locales = [locales];
    }
    // Create a copy of the array so we can push on the default locale.
    locales = (locales || []).concat(RelativeFormat.defaultLocale);
    var localeData = RelativeFormat.__localeData__;
    var i, len, localeParts, data;
    // Using the set of locales + the default locale, we look for the first one
    // which that has been registered. When data does not exist for a locale, we
    // traverse its ancestors to find something that's been registered within
    // its hierarchy of locales. Since we lack the proper `parentLocale` data
    // here, we must take a naive approach to traversal.
    for (i = 0, len = locales.length; i < len; i += 1) {
        localeParts = locales[i].toLowerCase().split('-');
        while (localeParts.length) {
            data = localeData[localeParts.join('-')];
            if (data) {
                // Return the normalized locale string; e.g., we return "en-US",
                // instead of "en-us".
                return data.locale;
            }
            localeParts.pop();
        }
    }
    var defaultLocale = locales.pop();
    throw new Error('No locale data has been added to IntlRelativeFormat for: ' +
        locales.join(', ') +
        ', or the default locale: ' +
        defaultLocale);
}
function findFields(locale) {
    var localeData = RelativeFormat.__localeData__;
    var data = localeData[locale.toLowerCase()];
    // The locale data is de-duplicated, so we have to traverse the locale's
    // hierarchy until we find `fields` to return.
    while (data) {
        if (data.fields) {
            return data.fields;
        }
        data = !!data.parentLocale
            ? localeData[data.parentLocale.toLowerCase()]
            : undefined;
    }
    throw new Error('Locale data added to IntlRelativeFormat is missing `fields` for :' + locale);
}
function resolveStyle(style) {
    // Default to "best fit" style.
    if (!style) {
        return 'best fit';
    }
    if (style === 'best fit' || style === 'numeric') {
        return style;
    }
    throw new Error("\"" + style + "\" is not a valid IntlRelativeFormat 'style' value, it must be one of: 'best fit' or 'numeric'");
}
function selectUnits(diffReport) {
    var fields = FIELDS.filter(function (field) {
        return field.indexOf('-short') < 1;
    });
    var units = fields[0];
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        units = fields_1[_i];
        if (Math.abs(diffReport[units]) < RelativeFormat.thresholds[units]) {
            break;
        }
    }
    return units;
}
var RelativeFormat = (function (locales, options) {
    if (options === void 0) { options = {}; }
    // Make a copy of `locales` if it's an array, so that it doesn't change
    // since it's used lazily.
    if (Array.isArray(locales)) {
        locales = locales.slice();
    }
    var locale = resolveLocale(locales);
    var resolvedOptions = {
        style: resolveStyle(options.style),
        units: isValidUnits(options.units) && options.units
    };
    var fields = findFields(locale);
    var messages = {};
    function getRelativeUnits(difference, units) {
        var field = fields[units];
        if (field.relative) {
            return field.relative[difference];
        }
    }
    function getMessage(units) {
        // Create a new synthetic message based on the locale data from CLDR.
        if (!messages[units]) {
            messages[units] = compileMessage(units);
        }
        return messages[units];
    }
    function compileMessage(units) {
        var relativeTime = fields[units].relativeTime;
        var future = Object.keys(relativeTime.future).reduce(function (future, i) {
            return future + (" " + i + " {" + relativeTime.future[i].replace('{0}', '#') + "}");
        }, '');
        var past = Object.keys(relativeTime.past).reduce(function (past, i) {
            return past + (" " + i + " {" + relativeTime.past[i].replace('{0}', '#') + "}");
        }, '');
        var message = '{when, select, future {{0, plural, ' +
            future +
            '}}' +
            'past {{0, plural, ' +
            past +
            '}}}';
        // Create the synthetic IntlMessageFormat instance using the original
        // locales value specified by the user when constructing the the parent
        // IntlRelativeFormat instance.
        return new IntlMessageFormat(message, locales);
    }
    return {
        format: function (date, options) {
            var now = options && options.now !== undefined
                ? options.now === null
                    ? 0
                    : options.now
                : Date.now();
            if (date === undefined) {
                date = now;
            }
            // Determine if the `date` and optional `now` values are valid, and throw a
            // similar error to what `Intl.DateTimeFormat#format()` would throw.
            if (!isFinite(now)) {
                throw new RangeError('The `now` option provided to IntlRelativeFormat#format() is not ' +
                    'in valid range.');
            }
            if (!isFinite(date)) {
                throw new RangeError('The date value provided to IntlRelativeFormat#format() is not ' +
                    'in valid range.');
            }
            var diffReport = diff(now, date);
            var units = resolvedOptions.units || selectUnits(diffReport);
            var diffInUnits = diffReport[units];
            if (resolvedOptions.style !== "numeric" /* numeric */) {
                var relativeUnits = getRelativeUnits(diffInUnits + '', units);
                if (relativeUnits) {
                    return relativeUnits;
                }
            }
            return getMessage(units).format({
                '0': Math.abs(diffInUnits),
                when: diffInUnits < 0 ? 'past' : 'future'
            });
        },
        resolvedOptions: function () {
            return {
                locale: locale,
                style: resolvedOptions.style,
                units: resolvedOptions.units
            };
        },
        _compileMessage: compileMessage
    };
});
RelativeFormat.__localeData__ = {};
RelativeFormat.__addLocaleData = function () {
    var data = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        data[_i] = arguments[_i];
    }
    for (var _a = 0, data_1 = data; _a < data_1.length; _a++) {
        var datum = data_1[_a];
        if (!(datum && datum.locale)) {
            throw new Error('Locale data provided to IntlRelativeFormat is missing a ' +
                '`locale` property value');
        }
        RelativeFormat.__localeData__[datum.locale.toLowerCase()] = datum;
    }
};
// Define public `defaultLocale` property which can be set by the developer, or
// it will be set when the first RelativeFormat instance is created by
// leveraging the resolved locale from `Intl`.
RelativeFormat.defaultLocale = 'en';
RelativeFormat.thresholds = {
    second: 45,
    'second-short': 45,
    minute: 45,
    'minute-short': 45,
    hour: 22,
    'hour-short': 22,
    day: 26,
    'day-short': 26,
    month: 11,
    'month-short': 11 // months to year
};
export default RelativeFormat;
//# sourceMappingURL=core.js.map